import React from "react";
import CommonProduct from "../components/commonPage"

class AboutPage extends CommonProduct {
    constructor(props) {
        super(props)
        this.firstRender = true
    }

    render() {
        let BaseComponent = this.BaseComponentRender.bind(this)
        return (
            <BaseComponent className="page" menu="about" title="About | EdTech Index">
                <h1>About</h1>
                <p className="strong">
                    A single source for edtech quality.
                </p>
                <p>
                    There are thousands of edtech products on the market. Vetting them all individually at the district or state level is no longer sustainable or necessary. The EdTech Index is a free, public resource that brings together independent validations from leading education organizations, researchers, and certification providers.
                </p>
                <p>
                    Instead of relying on vendor claims alone, educators and education leaders get a single source of truth: tools evaluated against five widely recognized quality indicators:
                </p>
                <ul className="aboutList">
                    <li><strong>Safe:</strong> Research-backed privacy and data security practices that protect students.</li>
                    <li><strong>Evidence-Based:</strong> Demonstrated effectiveness supported by independent research and evaluation.</li>
                    <li><strong>Inclusive:</strong> Designed to be accessible and supportive for all learners, including those with diverse needs.</li>
                    <li><strong>Usable:</strong> Easy for educators and students to implement successfully.</li>
                    <li><strong>Interoperable:</strong> Works seamlessly with the systems schools and districts already use. </li>
                </ul>
                <p>
                    Leading EdTech organizations have pledged to work together to reduce the burden on educators and school leaders. By identifying the Five EdTech Quality Indicators for evaluating effective EdTech products and supporting their inclusion on the EdTech Index, these organizations have demonstrated their commitment to informed decision-making:
                </p>
                <div id="initiative" className="partner_group">
                    <div className="partners">
                        <div className="partner_div"><a href="https://iste.org/" target="_blank"><img src="../images/new/iste-ascd_stacked_color_v1.png" alt="ISTE" /></a>                      </div>
                        <div className="partner_div"><a href="https://www.cosn.org/" target="_blank"><img src="../images/new/COSN_transparent.png" alt="CoSN" /></a>           </div>
                        <div className="partner_div"><a href="https://digitalpromise.org/" target="_blank"><img src="../images/new/DigitalPromise.png" alt="Digital Promise" /></a>                      </div>
                        <div className="partner_div"><a href="https://www.setda.org/" target="_blank"><img src="../images/new/sedta-2.png" alt="SETDA" /></a>                      </div>
                        <div className="partner_div"><a href="https://www.1edtech.org/" target="_blank"><img src="../images/new/1EdTechLogo.png" alt="1EdTech" /></a>           </div>
                        <div className="partner_div"><a href="https://www.cast.org/" target="_blank"><img src="../images/CAST-logo-purple.png" alt="CAST" /></a>           </div>
                        <div className="partner_div"><a href="https://www.innovateedunyc.org/" target="_blank"><img src="../images/new/InnovateEDU.png" alt="InnovateEDU" /></a>     </div>
                    </div>
                </div>
                <div className="clear dividingLine" />

                <p className="strong">
                    Who Is It For?
                </p>
                <p className="strong">
                    <em><strong>Educators</strong></em>
                </p>
                <p>
                    Find tools aligned to your instructional goals, compare options quickly, and make more confident classroom decisions.
                </p>
                <table className="aboutTable">
                    <tbody>
                        <tr>
                            <td>
                                <strong>There are thousands of edtech tools, and there’s no roadmap</strong><br /><br />
                                Educators are expected to identify, evaluate, and adopt technology that meaningfully improves learning. But with thousands of tools on the market and limited time, the discovery process is fragmented, inconsistent, and often driven by vendor marketing rather than evidence.
                            </td>
                            <td>
                                <strong>Public skepticism and low adoption are raising the stakes</strong><br /><br />
                                The value of education technology is being publicly questioned — from school boards to state legislatures. Educators face pressure to justify every tool they use while also navigating privacy concerns, equity gaps, needs for professional development, and a lack of time to vet solutions independently.
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p className="subhead">
                    <strong>Get all your questions answered before you adopt a tool</strong>
                </p>
                <ul className="aboutList">
                    <li>Will this actually improve learning?</li>
                    <li>Is it grounded in pedagogy?</li>
                    <li>Who else has evaluated this?</li>
                    <li>Is it safe for my students?</li>
                    <li>How does it compare to alternatives?</li>
                    <li>Is it easy enough for real classrooms?</li>
                </ul>
                <p className="subhead">
                    <strong>The first stop for finding, comparing, and confidently selecting edtech</strong>
                </p>
                <p>
                    The EdTech Index brings together the most complete, reliable, and up-to-date information on 2,000+ learning technology products — for free. It consolidates quality signals from trusted organizations into one place so educators can make informed decisions without starting from scratch.
                </p>
                <ul className="aboutList">
                    <li><strong>Curated directory with validation filters —</strong> filter by privacy, accessibility, interoperability, and research badges from Digital Promise, CAST, Project Unicorn, 1EdTech, and more.</li>
                    <li><strong>Teacher Ready evaluation framework —</strong> a standardized, research-grounded tool for assessing classroom usability, pedagogical alignment, student engagement, and digital equity.</li>
                    <li><strong>ISTE Seal indicator —</strong> quickly identify products certified for alignment to ISTE Standards, learning science, and inclusive design without reading every product's marketing claims.</li>
                    <li><strong>Side-by-side comparison and export —</strong> compare curated tool lists, export for team review, and share with administrators or in professional development sessions.</li>
                </ul>
                <p className="subhead subhead-district">
                    <em>Are you with a school district?</em>
                </p>
                <p>
                    Reduce the time spent vetting products and gain access to trusted quality indicators from independent organizations. The EdTech Index helps districts focus on local needs instead of duplicating evaluation work already happening across the field.
                </p>
                <p className="subhead subhead-district">
                    <em>Are you with a state education agency?</em>
                </p>
                <p>
                    Build and maintain state-specific edtech directories without creating new review systems from scratch. Leverage shared infrastructure while maintaining state priorities and requirements.
                </p>
                <p className="strong section-providers">
                    <em><strong>EdTech Providers</strong></em>
                </p>
                <p>
                    Showcase your products alongside trusted validations, certifications, and evidence that help educators make informed purchasing decisions.
                </p>
                <table className="aboutTable">
                    <tbody>
                        <tr>
                            <td>
                                <strong>The edtech market is crowded. How can you stand out?</strong><br /><br />
                                School districts face tighter budgets and greater accountability than ever before. They're evaluating every line item, and edtech vendors are expected to demonstrate measurable impact on student outcomes — not just product features — to earn a seat at the table.<br />
                            </td>
                            <td>
                                <strong>Skepticism is rising. So is legislation.</strong><br /><br />
                                Increased public scrutiny is making procurement harder. District buyers want third-party evidence, not vendor-produced marketing. Without recognized validation, even quality products get overlooked or blocked at the policy level before a demo is ever requested.<br />
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p className="subhead">
                    <strong>What are buyers demanding before they commit?</strong>
                </p>
                <ul className="aboutList">
                    <li>Is there evidence this works?</li>
                    <li>Is it aligned to ISTE Standards?</li>
                    <li>Has it been independently evaluated?</li>
                    <li>Is student data safe and private?</li>
                    <li>Does it integrate with our systems?</li>
                    <li>Is it accessible to all learners?</li>
                </ul>
                <p className="subhead">
                    <strong>A trusted stage for products that have earned their credibility</strong>
                </p>
                <p>
                    The EdTech Index — backed by ISTE — gives vendors a verified, high-visibility presence in front of a monthly audience of up to 45,000 edtech decision-makers. Listings surface third-party validation badges that answer district questions before they're even asked.
                </p>
                <ul className="aboutList">
                    <li><strong>ISTE Seal certification —</strong> signals alignment to ISTE Standards, research-based pedagogy, usability, and inclusivity to district buyers across the sector.</li>
                    <li><strong>Third-party validation badges —</strong> showcase credentials across privacy, accessibility, interoperability, and evidence of efficacy from trusted partner organizations.</li>
                    <li><strong>Premium profile options —</strong> expanded reach through state education agency (SEA) promotion and multimedia product showcases that stand out in a crowded directory.</li>
                    <li><strong>coLab community access —</strong> engage directly with educators, specialists, and leaders through focus groups, surveys, and co-design to build stronger evidence and product-market fit.</li>
                </ul>
                <br />
                <div className="clear dividingLine" />
                <p>
                    Gathering and dispersing this information is truly a collaborative effort, and we couldn't do this without the support of our funders and partners. Thank you to the following organizations who have partnered with us in our collective vision to improve effective EdTech discovery and decision making:
                </p>
                <br />
                <br />
                <div className="partners">
                    <div className="partner_div"><a href="https://ccsso.org/" target="_blank"><img src="../images/new/ccsso_new.png" alt="Council of Chief State School Officers" /></a>           </div>
                    <div className="partner_div"><a href="https://www.commonsensemedia.org/" target="_blank"><img src="../images/3.H-LOGO-Common_Sense-RGB.svg" alt="Common Sense" /></a>                  </div>
                    <div className="partner_div"><a href="https://educationmarkets.org/" target="_blank"><img src="../images/new/EMDctr.png" alt="CEMD" /></a>                                          </div>
                    <div className="partner_div"><a href="https://theatlis.org/" target="_blank"><img src="../images/ATLISLogo2023.png" alt="Association of Technology Leaders In Independent Schools" /></a></div>
                </div>

                <div className="clear" />
                <div className="disclaimer">*Our partners do not endorse any specific product listed in the Index.<br />
                    The 5 Edtech Quality Indicators framework is licensed under <a href="https://creativecommons.org/licenses/by-nc-nd/4.0/">CC BY-NC-ND 4.0</a>
                    <img src="https://mirrors.creativecommons.org/presskit/buttons/88x31/png/by-nc-nd.png" alt="" />
                </div>
                <br />
                <ul>
                    <li><a target="_blank" href="https://iste-ascd.org/terms-of-service">Terms of Use</a></li>
                    <li><a target="_blank" href="https://iste-ascd.org/privacy-policy">Privacy Policy</a></li>
                    <li><a target="_blank" href="https://iste-policies.s3.amazonaws.com/legal+documents/api_and_data_license_agreement.pdf">API &amp; Data License Agreement</a></li>
                    <li><a target="_blank" href="https://iste-policies.s3.amazonaws.com/legal+documents/Cookie+Policy.pdf">Cookie Policy</a></li>
                </ul>
                <div className="clear" />
            </BaseComponent>
        )
    }
}
export default AboutPage